<script>
import { GlButton, GlFormInput, GlTooltipDirective } from '@gitlab/ui';
import { getDraft, updateDraft } from '~/lib/utils/autosave';
import { s__ } from '~/locale';
import { stripDuoAnswerMarker } from '../../utils/duo_question';

export default {
  name: 'DuoCustomAnswerInput',
  components: { GlButton, GlFormInput },
  directives: { GlTooltip: GlTooltipDirective },
  props: {
    draftKey: {
      type: String,
      required: true,
    },
    inputId: {
      type: String,
      required: true,
    },
    disabled: {
      type: Boolean,
      required: false,
      default: false,
    },
    submitting: {
      type: Boolean,
      required: false,
      default: false,
    },
  },
  emits: ['submit'],
  data() {
    return { answer: getDraft(this.draftKey) ?? '' };
  },
  computed: {
    label() {
      return stripDuoAnswerMarker(this.answer);
    },
    canSubmit() {
      return this.label.length > 0 && !this.disabled;
    },
    tooltip() {
      return this.canSubmit ? '' : s__('WorkItemDuoQuestion|An answer is required to submit');
    },
  },
  watch: {
    answer(value) {
      updateDraft(this.draftKey, value);
    },
  },
  methods: {
    // Enter reaches this even when the button is disabled, so the guard is not
    // redundant with the button's `disabled`.
    submit() {
      if (this.canSubmit) this.$emit('submit', this.label);
    },
  },
};
</script>

<template>
  <div
    class="gl-flex gl-min-h-8 gl-items-center gl-gap-2 gl-rounded-lg gl-border-1 gl-border-solid gl-border-default gl-bg-default gl-py-1 gl-pr-2 focus-within:gl-focus dark:gl-bg-neutral-700"
  >
    <label :for="inputId" class="gl-sr-only">{{
      s__('WorkItemDuoQuestion|Answer in your own words')
    }}</label>
    <gl-form-input
      :id="inputId"
      v-model.trim="answer"
      :placeholder="s__('WorkItemDuoQuestion|Type your answer here…')"
      :disabled="disabled"
      class="!gl-bg-transparent !gl-shadow-none"
      data-testid="duo-custom-answer-input"
      @keydown.enter="submit"
    />
    <gl-button
      v-gl-tooltip="tooltip"
      :disabled="!canSubmit"
      :loading="submitting"
      :aria-label="s__('WorkItemDuoQuestion|Send answer')"
      variant="confirm"
      icon="paper-airplane"
      data-testid="duo-custom-answer-submit"
      @click="submit"
    />
  </div>
</template>
